<!--
View Dialog Field with two buttons and possible alert.

Easy to use dialog field for confirmations. Contains two configurable buttons and a settable alert-box at the top.
-->

<template>
  <v-dialog v-model="showDialog" persistent width="unset" :hide-overlay="hideOverlay">
    <v-alert v-if="showAlert" :type="alertType" class="mb-0">
      {{ alertText }}
    </v-alert>
    <v-alert v-if="showAlertProp" :type="alertTypeProp" class="mb-0">
      {{ alertTextProp }}
    </v-alert>
    <!-- Unexpected useless attribute on `<template>`  vue/no-useless-template-attributes
 @TODO check class="pa-0" -->
    <template v-if="!hideButton" v-slot:activator="{on}">
      <v-btn
          :color="color"
          :class="'mr-3 mb-3 mb-md-0 white--text ' + btnClass"
          v-on="on"
          :rounded="rounded"
          :outlined="outlined"
          elevation="0"
      >
        <v-icon class="mr-2" v-if="icon">{{icon}}</v-icon>
        {{ btnText }}
      </v-btn>
    </template>
    <v-card class="pa-0">
      <v-card-title class="headline">{{ dialogTitle }}</v-card-title>
      <v-card-subtitle class="pb-0" v-if="subtitleText">{{ subtitleText }}</v-card-subtitle>
      <v-card outlined class="ma-5" v-if="addInfoText">
        <v-card-text>
          <p>{{ addInfoText }}</p>
        </v-card-text>
      </v-card>
      <v-card-text v-if="addTextField">
        <v-container>
          <v-text-field
              v-model="textField"
              :label="addTextFieldLabel"
              outlined
          />
        </v-container>
      </v-card-text>
      <v-card-text v-if="!addTextField && !pureHtml">
        <div v-html="dialogText"></div>

        <!-- Animated dots -->
        <div v-if="animate" class="spinner">
          <div class="bounce1"></div>
          <div class="bounce2"></div>
          <div class="bounce3"></div>
        </div>
      </v-card-text>
      <v-card-text v-else>
        <div v-html="dialogText"></div>

        <!-- Animated dots -->
        <div v-if="animate" class="spinner">
          <div class="bounce1"></div>
          <div class="bounce2"></div>
          <div class="bounce3"></div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
            v-if="!hidePrimaryButton"
            :disabled="button1Disabled"
            :loading="button1Loading"
            color="primary"
            @click="clickedButton1()"
        >{{ button1Text }}
        </v-btn>
        <v-btn
            v-if="showRouteButton"
            color="primary"
            :to="toRoute"
        >{{ routeButtonText }}
        </v-btn>
        <v-btn
            v-if="!hideErrorButton"
            :disabled="button2Disabled"
            :loading="button2Loading"
            color="error"
            @click="clickedButton2()"
        >{{ button2Text }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "TwoButtonDialog",
  props: {
    btnText: String,
    btnClass: String,
    color: String,
    dialogTitle: String,
    dialogText: String,
    button1Text: String,
    button2Text: String,
    hideButton: Boolean,
    hidePrimaryButton: Boolean,
    hideErrorButton: Boolean,
    subtitleText: String,
    addInfoText: String,
    animate: Boolean,
    addTextField: Boolean,
    addTextFieldLabel: String,
    showRouteButton: Boolean,
    routeButtonText: String,
    hideOverlay: Boolean,
    showAlertProp: Boolean,
    alertTypeProp: String,
    alertTextProp: String,
    outlined: Boolean,
    icon: String,
    rounded: {
      default: true,
      type: Boolean
    },
    pureHtml: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      showDialog: false,
      button1Loading: false,
      button2Loading: false,
      button1Disabled: false,
      button2Disabled: false,
      showAlert: false,
      alertText: "",
      alertType: "",
      textField: "",
      propToGiveThrough: null,
      toRoute: String,
    }
  },
  methods: {
    clickedButton1() {
      if(this.propToGiveThrough) {
        this.$emit('clickedButton1', this.propToGiveThrough);
      } else {
        this.$emit('clickedButton1');
      }
    },
    clickedButton2() {
      if(this.propToGiveThrough) {
        this.$emit('clickedButton2', this.propToGiveThrough);
      } else {
        this.$emit('clickedButton2');
      }
    },
    setButtonSettings(button1Loading, button1Disabled, button2Loading, button2Disabled) {
      this.button1Loading = button1Loading;
      this.button1Disabled = button1Disabled;
      this.button2Loading = button2Loading;
      this.button2Disabled = button2Disabled;
    },
    setAlertSettings(type, text, show) {
      this.alertType = type;
      this.alertText = text;
      this.showAlert = show;
    },
    resetSettings() {
      this.setButtonSettings(false, false, false, false);
      this.showDialog = false;
      this.setAlertSettings("", "", false);
    },
    /**
     * handler for keyup event registered in event listener.<br>
     * &lt;v-dialog @keydown="onKeydown" ...> does not work because of &lt;v-card> vuetify bug <br>
     * see {@link https://stackoverflow.com/questions/62357619/vue-keydown-on-v-dialog-v-overlay-stops-working-when-a-v-card-is-present }
     * @param e keydown-event parameter
     */
    onKeyup(e) {
      if (this.showDialog) {      // only handle keyup-events on dialogs, which are actually shown
        if (e.key === "Enter") {
          this.clickedButton2();    // button2 should be always the primary action-button
        }
      }
    },
  },
  mounted() {
    // register keyup event
    document.addEventListener('keyup', this.onKeyup);
  },
  beforeDestroy() {
    document.removeEventListener('keyup',this.onKeyup);
  }
}
</script>
<style src="@/modules/core/components/TwoButtonDialog/TwoButtonDialog.scss" lang="scss"/>
